import React, { type ChangeEvent, type SyntheticEvent, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import { Checkbox } from '@atlaskit/checkbox';
import Pagination from '@atlaskit/pagination';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { Skeleton } from '@atlassian/jira-common-components-skeleton/src/styled.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { PROJECT_PICKER_PENDING_CHANGES_LIMIT } from '../../../../common/ui/constants';
import {
	type OnFetchParam,
	type ProjectInput,
	type ProjectPickerFilter,
	type ProjectPickerPage,
	ProjectFilterChoice,
	ProjectPickerState,
	ProjectStyleForInput,
} from '../../../../common/ui/types';
import { calculateRowsOnPage } from '../../../../common/utils';
import { messages } from './messages';

type ErrorProps = {
	onRetry: (onFetchParam: OnFetchParam) => void;
};

type ProjectOptionProps = ProjectInput & {
	onChange: (project: ProjectInput) => void;
	isDefaultPriorityScheme: boolean;
	testId?: string;
};

type ProjectListProps = {
	filter: ProjectPickerFilter;
	onFetch: (onFetchParam: OnFetchParam) => void;
	onInputChange: (arg0: ProjectInput) => void;
	onSelectAllInputChange: (checked: boolean) => void;
	page: ProjectPickerPage;
	pendingChanges: Record<string, boolean>;
	priorityScheme: {
		name: string;
		isDefault: boolean;
	};
	selectAllChecked: boolean;
	tooltipDelay?: number;
	testId?: string;
};

type ProjectPickerContentProps = ProjectListProps & {
	state: (typeof ProjectPickerState)[keyof typeof ProjectPickerState];
};

type SelectAllOptionProps = {
	checked: boolean;
	disabled: boolean;
	indeterminate: boolean;
	onChange: (checked: boolean) => void;
};

export const Error = ({ onRetry }: ErrorProps) => {
	const { formatMessage } = useIntl();

	return (
		<SectionMessage
			appearance="error"
			actions={[
				<SectionMessageAction key="retry-button" onClick={() => onRetry({})}>
					{formatMessage(messages.retry)}
				</SectionMessageAction>,
			]}
			title={formatMessage(messages.errorTitle)}
		>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p>{formatMessage(messages.errorMessage)}</p>
		</SectionMessage>
	);
};

export const Loader = () => (
	<Container>
		<Skeleton height="28px" width="532px" />
		<Skeleton height="28px" width="532px" />
		<Skeleton height="28px" width="532px" />
		<Skeleton height="28px" width="532px" />
		<Skeleton height="28px" width="532px" />
		<Skeleton height="28px" width="532px" />
		<Skeleton height="28px" width="532px" />
		<Skeleton height="28px" width="532px" />
		<Skeleton height="28px" width="532px" />
		<Skeleton height="28px" width="532px" />
		<Skeleton height="28px" width="532px" />
		<Skeleton height="28px" width="532px" />
		<Skeleton height="28px" width="532px" />
		<Skeleton height="28px" width="532px" />
	</Container>
);

export const ProjectOption = (props: ProjectOptionProps) => {
	const {
		avatarUrl,
		checked,
		disabled,
		id,
		isDefaultPriorityScheme,
		name,
		onChange,
		projectStyle,
		testId,
	} = props;

	const { formatMessage } = useIntl();

	return (
		<ProjectCheckbox
			checked={checked}
			disabled={disabled}
			isAssociatedWithDefaultScheme={isDefaultPriorityScheme}
		>
			<SelectedBorder
				checked={checked}
				disabled={disabled}
				isAssociatedWithDefaultScheme={isDefaultPriorityScheme}
			/>
			<input
				checked={checked}
				disabled={disabled}
				id={id}
				name={name}
				onChange={(event: ChangeEvent<HTMLInputElement>) =>
					onChange({ ...props, checked: event.target.checked })
				}
				type="checkbox"
			/>
			{/* eslint-disable jsx-a11y/label-has-associated-control */}
			<label htmlFor={name} data-testId={testId}>
				<Avatar appearance="square" size="xsmall" src={avatarUrl} />
				<span>
					<PrimaryDescription>{name}</PrimaryDescription>
					<SecondaryDescription>
						{projectStyle === ProjectStyleForInput.CMP
							? formatMessage(messages.companyManagedProject)
							: formatMessage(messages.teamManagedProject)}
					</SecondaryDescription>
				</span>
			</label>
		</ProjectCheckbox>
	);
};

export const SelectAllOption = ({
	checked,
	disabled,
	indeterminate,
	onChange,
}: SelectAllOptionProps) => {
	const { formatMessage } = useIntl();

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		const {
			target: { checked: isChecked },
		} = event;
		onChange(isChecked);
	};

	return (
		<SelectAllCheckbox checked={checked} disabled={disabled}>
			<SelectedBorder checked={checked} disabled={disabled} isAssociatedWithDefaultScheme={false} />
			<Checkbox
				isChecked={checked}
				isDisabled={disabled}
				isIndeterminate={indeterminate}
				onChange={handleChange}
				label={<SelectAllCheckboxLabel>{formatMessage(messages.selectAll)}</SelectAllCheckboxLabel>}
			/>
		</SelectAllCheckbox>
	);
};

export const ProjectList = ({
	filter,
	onFetch,
	onInputChange,
	onSelectAllInputChange,
	page,
	pendingChanges,
	priorityScheme,
	selectAllChecked,
	tooltipDelay,
	testId,
}: ProjectListProps) => {
	const { formatMessage } = useIntl();

	const {
		createSchemeAllProjectsFilterInfo,
		editSchemeAllProjectsFilterInfo,
		existingProjectsFilterInfo,
		maxSelectionLimit,
		paginationNextLabel,
		paginationPageLabel,
		paginationPreviousLabel,
		paginationSummary,
		projectIsAssociatedWithDefaultScheme,
		projectIsAssociatedWithScheme,
	} = messages;

	const { projectFilterChoice } = filter;
	const { currentPage, items, totalItems, pageSize } = page;
	const { name, isDefault } = priorityScheme;

	// Pagination
	const { startRow, endRow } = calculateRowsOnPage({
		rowsPerPage: pageSize,
		selectedPage: currentPage,
		totalRows: totalItems,
	});
	const pageCount = Math.ceil(totalItems / pageSize);
	const pages = Array.from({ length: pageCount }, (_, index) => index + 1);

	const handleChange = (_event: SyntheticEvent<Element, Event>, selectedPage: number) => {
		onFetch({ selectedPage });
	};

	// Select All Option
	const availableOptions = items.filter((project) => !project.disabled);
	const availableOptionCount = availableOptions.length;
	const isEveryOptionDisabled = items.every((project) => project.disabled);
	const selectedOptionCount = availableOptions.filter(({ checked }) => checked).length;
	const validOptionRemainingCount = availableOptions.filter(
		({ id }) => !(id in pendingChanges),
	).length;
	const isSelectAllDisabled =
		isEveryOptionDisabled ||
		Object.values(pendingChanges).length + validOptionRemainingCount >
			PROJECT_PICKER_PENDING_CHANGES_LIMIT;

	return (
		<Container>
			<SectionMessage>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p>
					{projectFilterChoice === ProjectFilterChoice.ALL_PROJECTS
						? `${
								name
									? formatMessage(editSchemeAllProjectsFilterInfo, {
											schemeName: name,
										})
									: formatMessage(createSchemeAllProjectsFilterInfo)
							}`
						: formatMessage(existingProjectsFilterInfo, { schemeName: name })}
				</p>
			</SectionMessage>
			<Options>
				<SelectAllOption
					checked={selectAllChecked}
					disabled={isSelectAllDisabled}
					indeterminate={selectedOptionCount > 0 && selectedOptionCount < availableOptionCount}
					onChange={onSelectAllInputChange}
				/>
				{items.map((item) =>
					item.disabled ? (
						<Tooltip
							content={
								isDefault
									? formatMessage(projectIsAssociatedWithDefaultScheme)
									: formatMessage(maxSelectionLimit)
							}
							key={item.id}
							delay={tooltipDelay}
						>
							<ProjectOption
								isDefaultPriorityScheme={isDefault}
								onChange={onInputChange}
								testId={testId}
								{...item}
							/>
						</Tooltip>
					) : (
						<Tooltip
							content={
								projectFilterChoice === ProjectFilterChoice.ALL_PROJECTS &&
								item.isAssociatedWithScheme &&
								formatMessage(projectIsAssociatedWithScheme)
							}
							key={item.id}
							delay={tooltipDelay}
						>
							<ProjectOption
								isDefaultPriorityScheme={isDefault}
								onChange={onInputChange}
								testId={testId}
								{...item}
							/>
						</Tooltip>
					),
				)}
			</Options>
			<Pagination
				max={pageSize}
				nextLabel={formatMessage(paginationNextLabel)}
				label={formatMessage(paginationPageLabel)}
				onChange={handleChange}
				pageLabel={formatMessage(paginationPageLabel)}
				pages={pages}
				previousLabel={formatMessage(paginationPreviousLabel)}
				selectedIndex={currentPage - 1}
			/>
			<Summary>
				{formatMessage(paginationSummary, {
					startRow,
					endRow,
					totalRows: totalItems,
				})}
			</Summary>
		</Container>
	);
};

export const ProjectPickerContent = ({
	filter,
	onFetch,
	onInputChange,
	onSelectAllInputChange,
	page,
	pendingChanges,
	priorityScheme,
	selectAllChecked,
	state,
}: ProjectPickerContentProps) => {
	const { formatMessage } = useIntl();

	const { projectFilterChoice, query } = filter;
	const { items } = page;

	if (state === ProjectPickerState.LOADING) {
		return <Loader />;
	}

	if (state === ProjectPickerState.ERROR) {
		return <Error onRetry={onFetch} />;
	}

	const noProjectsInJira =
		!items.length && !query && projectFilterChoice === ProjectFilterChoice.ALL_PROJECTS;

	if (noProjectsInJira) {
		return <p>{formatMessage(messages.noProjectsInJira)}</p>;
	}

	const noSearchResultsFound = !items.length && query;

	if (noSearchResultsFound) {
		return <p>{formatMessage(messages.noSearchResults)}</p>;
	}

	return (
		<ProjectList
			filter={filter}
			onFetch={onFetch}
			onInputChange={onInputChange}
			onSelectAllInputChange={onSelectAllInputChange}
			page={page}
			pendingChanges={pendingChanges}
			priorityScheme={priorityScheme}
			selectAllChecked={selectAllChecked}
		/>
	);
};

const layoutStyles = {
	alignItems: 'center',
	display: 'flex',
	gap: `${token('space.100', '8px')}`,
};

const checkboxStyles = {
	...layoutStyles,
	borderBottom: `${token('space.025', '2px')} solid ${token('color.border', '#091E4224')}`,
	height: `${token('space.500', '40px')}`,
	width: '100%',
};

const columnLayoutStyles = {
	...layoutStyles,
	flexDirection: 'column',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const Container = styled.div(columnLayoutStyles);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Options = styled.div<{
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- PLEASE FIX - ENABLING FLAT LINT CONFIG
	font: token('font.body.small', fontFallback.body.small),
	marginBottom: `${token('space.100', '8px')}`,
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PrimaryDescription = styled.p<{
	children?: ReactNode;
}>({
	color: `${token('color.text.subtle', '#44546F')}`,
	fontWeight: '600',
});

const getProjectCheckboxBackgroundColor = (
	checked: boolean,
	disabled: boolean,
	isAssociatedWithDefaultScheme: boolean,
) => {
	let backgroundColor;

	if (disabled || (isAssociatedWithDefaultScheme && checked)) {
		backgroundColor = token('color.background.accent.gray.subtlest', '#F1F2F4');
	} else if (checked) {
		backgroundColor = token('color.background.accent.blue.subtlest', '#E9F2FF');
	} else {
		backgroundColor = 'transparent';
	}

	return backgroundColor;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProjectCheckbox = styled.div<{
	checked: boolean;
	children?: ReactNode;
	disabled: boolean;
	isAssociatedWithDefaultScheme: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...checkboxStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ checked, disabled, isAssociatedWithDefaultScheme }) =>
		getProjectCheckboxBackgroundColor(checked, disabled, isAssociatedWithDefaultScheme),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	input: {
		margin: `${token('space.0', '0px')} ${token('space.075', '6px')}`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	label: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		...layoutStyles,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	p: {
		margin: `${token('space.0', '0px')}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SecondaryDescription = styled.p<{
	children?: ReactNode;
}>({
	color: `${token('color.text.subtlest', '#626F86')}`,
});

const getSelectAllCheckboxBackgroundColor = (checked: boolean, disabled: boolean) => {
	let backgroundColor;

	if (disabled) {
		backgroundColor = token('color.background.accent.gray.subtlest', '#F1F2F4');
	} else if (checked) {
		backgroundColor = token('color.background.accent.blue.subtlest', '#E9F2FF');
	} else {
		backgroundColor = 'transparent';
	}

	return backgroundColor;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectAllCheckbox = styled.div<{
	checked: boolean;
	disabled: boolean;
	children?: ReactNode;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...checkboxStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ checked, disabled }) =>
		getSelectAllCheckboxBackgroundColor(checked, disabled),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	label: {
		alignContent: 'center',
		gap: `${token('space.100', '8px')}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectAllCheckboxLabel = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- PLEASE FIX - ENABLING FLAT LINT CONFIG
	font: token('font.body.small', fontFallback.body.small),
	fontWeight: '600',
});

const getSelectedBorderBackgroundColor = (
	checked: boolean,
	disabled: boolean,
	isAssociatedWithDefaultScheme: boolean,
) => {
	let backgroundColor;

	if (disabled || (isAssociatedWithDefaultScheme && checked)) {
		backgroundColor = token('color.border.disabled', '#091E420F');
	} else if (checked) {
		backgroundColor = token('color.border.selected', '#0C66E4');
	} else {
		backgroundColor = 'transparent';
	}

	return backgroundColor;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectedBorder = styled.span<{
	checked: boolean;
	disabled: boolean;
	isAssociatedWithDefaultScheme: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ checked, disabled, isAssociatedWithDefaultScheme }) =>
		getSelectedBorderBackgroundColor(checked, disabled, isAssociatedWithDefaultScheme),
	height: `${token('space.500', '40px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	visibility: ({ checked, disabled, isAssociatedWithDefaultScheme }) =>
		checked || disabled || isAssociatedWithDefaultScheme ? 'visible' : 'hidden',
	width: `${token('space.025', '2px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Summary = styled.p<{
	children?: ReactNode;
}>({
	color: `${token('color.text.subtle', '#44546F')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- PLEASE FIX - ENABLING FLAT LINT CONFIG
	font: token('font.body.small', fontFallback.body.small),
	margin: `${token('space.0', '0px')}`,
});
